import { Navigate, useLocation } from 'react-router-dom';

import { useSession } from '@/lib/session';
import { Skeleton } from '@/components/ui/Skeleton';
import { WrongDoor } from './WrongDoor';

/**
 * The console gate, for sellers and riders.
 *
 * The API is the security boundary — `auth:sanctum` plus the `role` middleware
 * refuse an unauthorised caller whatever this renders. This exists so somebody
 * on the wrong account sees a sentence explaining that, rather than a screen of
 * skeletons that never resolve, which is what an unhandled 403 actually looks
 * like from the outside.
 *
 * Admins pass every gate deliberately: when a pharmacy rings at 9pm with a dead
 * tablet and a rider waiting, somebody at ChiCare has to be able to move the
 * order, and a support team that must edit the database to do its job will
 * eventually edit the wrong row.
 */
export function RequireRole({
  role,
  signInPath,
  children,
}: {
  role: 'pharmacy' | 'rider';
  signInPath: string;
  children: React.ReactNode;
}) {
  const { isLoading, isUnauthenticated, user } = useSession();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[900px] space-y-4 px-4 py-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-56 w-full" radius="card" />
      </div>
    );
  }

  if (isUnauthenticated) {
    // Remember where they were headed, so signing in lands them there rather
    // than dumping them at the top of the console.
    return <Navigate to={signInPath} replace state={{ from: location.pathname }} />;
  }

  const allowed = [role, 'admin', 'super_admin'];

  if (!user || !allowed.includes(user.role)) {
    return (
      <WrongDoor
        title={role === 'pharmacy' ? 'This area is for pharmacies' : 'This area is for riders'}
        user={user}
      />
    );
  }

  return <>{children}</>;
}
